import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { isEmpty, partial, throttle } from 'lodash';
import PropTypes from 'prop-types';

import * as applicantMessengerActions from 'actions/applicantMessenger';

import {
  getDrawerApplicationConversations,
  getIsFetchingApplicationConversations,
} from 'selectors/entities';
import { getHiringManageApplicantsPath } from 'selectors/session';

import { toggleMessengerShown } from 'features/messengerv2/actions';
import ApplicantMessengerView from 'features/messengerv2/ApplicationConversationsView/ApplicantMessengerView/ApplicantMessengerView';
import {
  MessageDrawerListItem,
  MessageDrawerListView,
} from 'features/messengerv2/MessageDrawerList';
import { getDisplayTime } from 'features/messengerv2/util';

import Box from 'components/Box';
import Icon from 'components/Icon';
import Text from 'components/Text';

import cxHelpers from 'util/className';
import { toI18n } from 'util/i18n';
import { EVENT_ACTIONS, EVENT_CATEGORIES } from 'util/tracking_constants';

import { APPLICATION_MESSENGER_VIEWS } from '../constants';
import { selectCurrentApplicationMessageView } from '../selectors';

const THROTTLE_WAIT = 16;

@connect(
  (state, props) => ({
    applicationConversations: getDrawerApplicationConversations(state, props),
    isLoading: getIsFetchingApplicationConversations(state, props),
    manageHiringApplicantsPath: getHiringManageApplicantsPath(state, props),
    currentApplicationMessageView: selectCurrentApplicationMessageView(
      state,
      props
    ),
  }),
  {
    onToggle: toggleMessengerShown,
    onChangeApplicationMessengerView:
      applicantMessengerActions.showApplicantMessenger,
    onFetchConversations: applicantMessengerActions.fetchConversations,
  }
)
@cxHelpers('ApplicationConversationsView')
export default class ApplicationConversationsView extends Component {
  static propTypes = {
    isLoading: PropTypes.bool.isRequired,
    applicationConversations: ImmutablePropTypes.list,
    manageHiringApplicantsPath: PropTypes.string.isRequired,
    currentApplicationMessageView: PropTypes.string.isRequired,
    onChangeApplicationMessengerView: PropTypes.func.isRequired,
    currentApplicationId: PropTypes.number,
  };

  componentDidMount() {
    this.fetchPaginatedConversations();
  }

  componentWillUnmount() {
    if (this.container) this.unbindScrollListener();
  }

  shouldComponentUpdate(nextProps) {
    return !(
      !this.props.isLoading &&
      nextProps.isLoading &&
      this.props.applicationConversations.size > 0
    );
  }

  handleScroll = () => {
    if (this.shouldFetchMore()) {
      this.fetchPaginatedConversations();
    }
  };

  shouldFetchMore() {
    const { scrollTop, scrollHeight, offsetHeight } = this.container;

    return (
      !(
        this.props.isLoading ||
        this.lastFetchedConversationId === this.getLastConversationId()
      ) && scrollTop + offsetHeight >= scrollHeight
    );
  }

  fetchPaginatedConversations() {
    this.lastFetchedConversationId = this.getLastConversationId();
    this.props.onFetchConversations(this.lastFetchedConversationId);
  }

  getLastConversationId() {
    const conversations = this.props.applicationConversations;
    const conversationIds = conversations.map(c => c.get('id')).toArray();
    return isEmpty(conversationIds) ? undefined : Math.min(...conversationIds);
  }

  handleClose = () => this.props.onToggle(false);
  handleShow = applicationId =>
    this.props.onChangeApplicationMessengerView(applicationId, true);
  handleReturn = () => this.props.onChangeApplicationMessengerView(null);

  renderEmptyState() {
    return (
      <Box mt48 paxs tcenter>
        <Icon size={48} type="userO" color="blueLight" />
        <Box tcenter pt16>
          <Text
            fs16
            grayXdark
            i18n="message_drawer.applicants.empty_state.title"
          />
        </Box>
      </Box>
    );
  }

  throttledHandleScroll = throttle(this.handleScroll, THROTTLE_WAIT);

  getContainerRef = container => {
    if (this.container) this.unbindScrollListener();
    if (container) this.bindScrollListener(container);
    this.container = container;
  };

  bindScrollListener = container => {
    findDOMNode(container).addEventListener(
      'scroll',
      this.throttledHandleScroll
    );
  };

  unbindScrollListener = () => {
    findDOMNode(this.container).removeEventListener(
      'scroll',
      this.throttledHandleScroll
    );
  };

  renderRow = item => {
    const role = item.get('application_role');
    const location = item.get('location_name');
    const lastMessage = item.get('messages').first();

    return (
      <MessageDrawerListItem
        key={item.get('id')}
        title={lastMessage.get('applicant_full_name')}
        subTitle={lastMessage.get('body')}
        footer={role ? `${role} ${toI18n('at')} ${location}` : location}
        time={getDisplayTime(lastMessage.get('created_at_sec'))}
        shouldShowBullet
        hasUnread={item.get('has_unread') > 0}
        handleClick={partial(this.handleShow, item.get('job_application_id'))}
      />
    );
  };

  renderBody() {
    const {
      applicationConversations,
      isLoading,
      currentApplicationMessageView,
    } = this.props;

    return currentApplicationMessageView ===
      APPLICATION_MESSENGER_VIEWS.INDEX ? (
      <MessageDrawerListView
        isLoading={isLoading}
        ref={this.getContainerRef}
        className={this.cx()}
        dataSource={applicationConversations}
        emptyState={this.renderEmptyState()}
        renderRow={this.renderRow}
        footer={null}
        trackEventAction={EVENT_ACTIONS.PAGE_VIEWED}
        eventCategory={EVENT_CATEGORIES.APPLICANT_MESSAGES}
      />
    ) : (
      <ApplicantMessengerView
        onReturn={this.handleReturn}
        onClose={this.handleClose}
      />
    );
  }

  render() {
    return this.renderBody();
  }
}
